'use client'

import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { DrawerAction } from '@/ui/drawer/drawer/Drawer.client'
import { Form, useForm, useWatch } from '@/ui/form/Form.client'
import { Select } from '@/ui/form/inputs/select/Select.client'
import { TextAreaInput } from '@/ui/form/inputs/textarea/TextAreaInput.client'
import { useToast } from '@/ui/toast/Toast'
import { type ProductAnomaly } from '@gql-generated/graphql'
import { useAtom } from 'jotai'
import { reportProductAnomalyAction } from '../../services/product.action'
import { displayBobDrawerAtom } from '../../services/product.store'

export type BobFormValues = {
  anomalyType: string
  anomalyDetail: string
  anomalyDescription?: string
}

export type BobFormProps = {
  anomalies: ProductAnomaly[]
  reference: string
}

export function BobForm({ anomalies, reference }: BobFormProps) {
  const { t } = useTranslation(['BobDrawer', 'generic'])
  const toast = useToast()
  const [, setIsDrawerOpen] = useAtom(displayBobDrawerAtom)

  const form = useForm<BobFormValues>({})
  const { control, reset, handleSubmit } = form

  const selectedAnomalyTypeValue = useWatch({ control, name: 'anomalyType' })
  const selectedAnomalyType = anomalies.find((a) => a.value === selectedAnomalyTypeValue)

  const selectedAnomalyDetailValue = useWatch({ control, name: 'anomalyDetail' })
  const selectedAnomalyDetail = selectedAnomalyType?.categories?.find(
    (cat) => cat.value === selectedAnomalyDetailValue,
  )

  const handleResetAndCancel = () => {
    reset({ anomalyType: '', anomalyDetail: '', anomalyDescription: '' })
    setIsDrawerOpen(false)
  }

  const onSubmit = async (data: BobFormValues) => {
    try {
      pushToGtmDataLayer({
        event: 'gaEvent',
        eventCategory: 'fiche article / produit',
        eventAction: 'bouton bob',
        eventLabel: 'validation envoi formulaire',
      })

      const isReportSent = await reportProductAnomalyAction(
        reference,
        data.anomalyType,
        data.anomalyDetail,
        window.location.href,
        data.anomalyDescription ?? '',
      )

      const status = isReportSent ? 'success' : 'error'
      toast[status]({
        title: t(`BobDrawer:toast.titles.${status}`),
        message: t(`BobDrawer:toast.messages.${status}`),
      })
    } catch (error) {
      console.error('Could not submit form using api: ', error) // TODO: Logerrors
    } finally {
      handleResetAndCancel()
    }
  }

  return (
    <Form form={form} onSubmitAction={onSubmit} className="flex flex-col gap-1">
      <Select
        name="anomalyType"
        label={t('BobDrawer:form.anomalyTypeSelectLabel')}
        items={anomalies.map((anomaly) => ({ id: anomaly.value, label: anomaly.name }))}
        placeholder={t('BobDrawer:form.selectPlaceholder')}
        required
      />

      {selectedAnomalyType?.categories && (
        <Select
          name="anomalyDetail"
          label={t('BobDrawer:form.anomalyDetailSelectLabel')}
          items={selectedAnomalyType.categories.map((cat) => ({ id: cat.value, label: cat.name }))}
          placeholder={t('BobDrawer:form.selectPlaceholder')}
          required
        />
      )}

      {selectedAnomalyType && (!selectedAnomalyType.categories || selectedAnomalyDetail) && (
        <>
          <TextAreaInput
            name="anomalyDescription"
            label={t('BobDrawer:form.textareaLabel')}
            maxLength={500}
          />
          <DrawerAction type="cancel">{t('generic:actions.cancel')}</DrawerAction>
          <DrawerAction
            handle="validate"
            type="custom"
            variant="primary"
            action={handleSubmit(onSubmit)}
          >
            {t('generic:actions.validate')}
          </DrawerAction>
        </>
      )}
    </Form>
  )
}
